import { DEFAULT_COMMON_DATA, DEFAULT_CONFIG, UNKNOWN_VALUE } from './constant/config';
import { createPipeline, sendNormalLogPipe, throttlePipe, errorLogLimitPipe } from './pipes/index';
import { generateUId, getDeviceId, getNetworkType, getPlatform } from './util/index';
import { EventEmitter } from './util/eventEmitter';
import { lifeCycleEventName, NetworkType, SeverityType } from './constant';
import { errorDetectionPlugin } from './plugins/onErrorPlugin';
import { webVitalsPlugin } from './plugins/webVitalsPlugin';
import { assetSpeedPlugin } from './plugins/assetsSpeedPlugin';
export default class AirTracker {
    constructor({ config, plugins = [] }) {
        this.config = DEFAULT_CONFIG;
        this.lifeCycle = new EventEmitter();
        this.plugins = [];
        this._commonData = DEFAULT_COMMON_DATA;
        // customized speed log
        this.timeMap = {};
        this.detectHrefChange = () => {
            let previousUrl = location.href;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;
            const observer = new MutationObserver(function () {
                if (location.href !== previousUrl) {
                    previousUrl = location.href;
                    that.updateCommonData({ currentHref: location.href });
                }
            });
            const config = { subtree: true, childList: true };
            observer.observe(document, config);
        };
        this.normalLogPipeLine = createPipeline([throttlePipe(this, 5), errorLogLimitPipe(this), sendNormalLogPipe(this)]);
        this.plugins = [...plugins];
        this.setConfig(config);
        this.initCommonData(config);
        this.lifeCycle.emit(lifeCycleEventName.onInit);
        this.detectHrefChange();
        // if allow enableErrorMonitoring, then enable errorDetectionPlugin
        if (config.enableErrorMonitoring) {
            this.plugins.push(errorDetectionPlugin);
        }
        if (config.enableWebVitals) {
            this.plugins.push(webVitalsPlugin);
        }
        if ((config === null || config === void 0 ? void 0 : config.assetSpeedMonitoringWhiteList) && config.assetSpeedMonitoringWhiteList.length > 0) {
            this.plugins.push(assetSpeedPlugin);
        }
        this.installPlugins(); // install all plugins
    }
    installPlugins() {
        this.plugins.forEach((item) => {
            item.patch(this);
        });
    }
    get commonData() {
        return this._commonData;
    }
    initCommonData(config) {
        this._commonData.sessionId = generateUId();
        this._commonData.deviceId = getDeviceId() || UNKNOWN_VALUE;
        this._commonData.platform = getPlatform();
        this._commonData.networkType = getNetworkType() || NetworkType.unknown;
        this._commonData.env = config.env || this._commonData.env;
        this._commonData.accountId = config.accountId || UNKNOWN_VALUE;
        this._commonData.appVersion = config.appVersion;
        this._commonData.appName = config.appName;
        this._commonData.currentHref = location.href;
        this.lifeCycle.emit(lifeCycleEventName.onCommonDataInit, this.commonData);
    }
    updateCommonDataBasedOnConfig(config) {
        this._commonData.env = config.env || this._commonData.env;
        this._commonData.appName = config.appName || this._commonData.appName;
        this._commonData.env = config.env || this._commonData.env;
        this._commonData.accountId = config.accountId || this._commonData.accountId;
        this._commonData.appVersion = config.appVersion || this._commonData.appVersion;
        this.lifeCycle.emit(lifeCycleEventName.onCommonUpdated, this._commonData);
    }
    // Can add extra common data based on developers' needs
    updateCommonData(extraData) {
        this._commonData = Object.assign(Object.assign({}, this._commonData), extraData);
    }
    // support set config later
    setConfig(config) {
        const setValue = (key, value) => {
            this.config[key] = value;
        };
        Object.entries(config).forEach((item) => {
            const [key, val] = item;
            if (typeof val !== undefined) {
                setValue(key, val);
            }
        });
        this.lifeCycle.emit(lifeCycleEventName.onConfigInit, this.config);
        this.updateCommonDataBasedOnConfig(config);
    }
    // send info level log
    info(eventName, extraInfo) {
        this.normalLogPipeLine(Object.assign({ severity: SeverityType.INFO, eventName }, extraInfo));
    }
    // send warn level log
    warn(eventName, extraInfo) {
        this.normalLogPipeLine(Object.assign({ severity: SeverityType.WARN, eventName }, extraInfo));
    }
    // send error level log
    error(eventName, extraInfo) {
        this.normalLogPipeLine(Object.assign({ severity: SeverityType.ERROR, eventName }, extraInfo));
    }
    // duration: milliseconds
    reportDuration(eventName, duration) {
        if (typeof eventName !== 'string') {
            console.warn('reportDuration: eventName (first param) must be a string');
            return;
        }
        if (typeof duration !== 'number') {
            console.warn('reportDuration: duration (second param) must be number');
            return;
        }
        if (duration < 0 || duration > 60000) {
            console.warn('reportDuration: duration (second param) must between 0 and 60000');
            return;
        }
        this.normalLogPipeLine({
            severity: SeverityType.SPEED,
            eventName,
            duration: duration,
        });
    }
    timeStart(eventName) {
        if (typeof eventName !== 'string') {
            console.warn('time: first param must be a string');
            return;
        }
        if (!this.timeMap[eventName]) {
            this.timeMap[eventName] = Date.now();
        }
        else {
            console.warn(`Timer ${eventName} already exists`);
        }
    }
    timeEnd(eventName) {
        if (typeof eventName !== 'string') {
            console.warn('timeEnd: first param must be a string');
            return;
        }
        if (this.timeMap[eventName]) {
            this.normalLogPipeLine({
                severity: SeverityType.SPEED,
                eventName,
                duration: Date.now() - this.timeMap[eventName],
            });
            delete this.timeMap[eventName];
        }
        else {
            console.warn(`Timer key :${eventName} does not exist`);
        }
    }
}
